"use client";

import { CtaArrow } from "@/components/editorial/cta-arrow";

import { useState } from "react";
import Image from "next/image";
import { Terminal } from "lucide-react";
import { TwinMark } from "@product/components/app/twin/twin-mark";
import styles from "./explorers.module.css";

const CLIENTS = ["Twin workspace", "ChatGPT", "Claude", "Terminal"] as const;

export default function InterfacesExplorer() {
  const [selected, setSelected] = useState(0);
  return (
    <div className={styles.explorer}>
      <div className={styles.eyebrow}>
        <TwinMark /> 01 / Change the interface
      </div>
      <h2 className={styles.heading}>A different door. The same definition.</h2>
      <p className={styles.intro}>
        Switch the presentation. The reviewed meaning, organisation scope and
        evidence requirements stay on the Twin side.
      </p>
      <div
        className={styles.choices}
        role="group"
        aria-label="Choose an interface"
      >
        {CLIENTS.map((name, i) => (
          <button
            key={name}
            aria-pressed={selected === i}
            onClick={() => setSelected(i)}
          >
            {name}
          </button>
        ))}
      </div>
      <div className={styles.interfaceGrid}>
        <div className={styles.client} aria-live="polite" aria-atomic="true">
          <div className={styles.clientHead}>
            {selected === 0 ? (
              <TwinMark />
            ) : selected === 3 ? (
              <Terminal aria-hidden />
            ) : (
              <Image
                src={`/concepts/source-icons/${selected === 1 ? "chatgpt" : "claude"}.svg`}
                alt=""
                width={22}
                height={22}
              />
            )}
            <span>{CLIENTS[selected]}</span>
          </div>
          {selected === 3 ? (
            <pre>
              <code>
                {
                  '$ twin query --question "What was net revenue retention in Q2?"\n\nExample answer\n118.0%\n\nDefinition: net revenue retention\nVersion:    v3\nEvidence:   inspect the answer receipt'
                }
              </code>
            </pre>
          ) : (
            <>
              <div className={styles.question}>
                What was net revenue retention in Q2?
              </div>
              <strong className={styles.number}>
                118<small>%</small>
              </strong>
              <p>
                {selected === 0
                  ? "The reviewed definition includes expansion and churn for the opening customer cohort."
                  : "Using the Twin's reviewed definition, Q2 net revenue retention was 118%."}
              </p>
              <span className={styles.eyebrow}>
                Twin context · definition v3
              </span>
            </>
          )}
        </div>
        <div className={styles.contract}>
          <span className={styles.eyebrow}>What stays the same</span>
          <dl className={styles.ledger}>
            <div>
              <dt>Meaning</dt>
              <dd>Net revenue retention · v3</dd>
            </div>
            <div>
              <dt>Scope</dt>
              <dd>The signed-in organisation</dd>
            </div>
            <div>
              <dt>Authority</dt>
              <dd>The caller's permitted access</dd>
            </div>
            <div>
              <dt>Evidence</dt>
              <dd>The answer's supported provenance</dd>
            </div>
          </dl>
          <p>
            An interface cannot grant itself wider access or invent a new
            business definition.
          </p>
        </div>
      </div>
      <div className={styles.limit}>
        Illustrative presentations of the same example, not live client sessions
        or guaranteed identical wording. Connections require organisation
        enablement and protected sign-in.{" "}
        <a href="https://docs.mlx.systems/docs/connect/availability">
          Check connection availability <CtaArrow external />
        </a>
      </div>
    </div>
  );
}
